/**
 * MakeLoveLocks — Love Lock Map (Mapbox GL JS)
 * Dark-themed interactive map with gold pin markers
 */

(function () {
  "use strict";

  const container = document.getElementById("mll-love-lock-map");
  if (!container) return;

  const token = container.dataset.mapboxToken;
  const appUrl = (container.dataset.appUrl || "https://make-interactive.fly.dev").replace(
    /\/$/,
    "",
  );
  const centerLat = parseFloat(container.dataset.centerLat) || 40;
  const centerLng = parseFloat(container.dataset.centerLng) || -30;
  const zoom = parseInt(container.dataset.zoom) || 3;

  if (!token) {
    container.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:rgba(249,249,249,0.4);font-size:0.9rem;">Map requires a Mapbox access token. Add it in the theme editor.</div>';
    return;
  }

  // Load Mapbox GL JS
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.css";
  document.head.appendChild(link);

  const script = document.createElement("script");
  script.src = "https://api.mapbox.com/mapbox-gl-js/v3.3.0/mapbox-gl.js";
  script.onload = initMap;
  document.head.appendChild(script);

  function initMap() {
    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: "mll-love-lock-map",
      style: "mapbox://styles/mapbox/light-v11",
      center: [centerLng, centerLat],
      zoom: zoom,
      minZoom: 1.5,
      maxZoom: 16,
      attributionControl: true,
      scrollZoom: false,
      projection: "mercator",
    });

    map.addControl(
      new mapboxgl.NavigationControl({
        showCompass: false,
      }),
      "top-right",
    );

    // Load pins from API and initialize
    fetch(appUrl + "/api/storefront/pins")
      .then((r) => r.json())
      .then((data) => {
        const samplePins = (data.pins || []).map((p) => ({
          names: p.names,
          city: p.city || "",
          country: p.country || "",
          message: p.message || "",
          photoUrl: p.photoUrl || "",
          lat: p.latitude,
          lng: p.longitude,
        }));
        initPinsOnMap(samplePins, map);
      })
      .catch((err) => {
        console.warn("[MLL Map] Failed to load pins from API, using empty set:", err);
        initPinsOnMap([], map);
      });
  }

  function initPinsOnMap(samplePins, map) {
    // Wait for map to load before adding pins
    map.on("load", () => {
      // Add pins as a GeoJSON source + circle layer for better performance
      const geojsonData = {
        type: "FeatureCollection",
        features: samplePins.map((pin, i) => ({
          type: "Feature",
          properties: {
            id: i,
            names: pin.names,
            city: pin.city,
            country: pin.country,
            message: pin.message || "",
            photoUrl: pin.photoUrl || "",
          },
          geometry: {
            type: "Point",
            coordinates: [pin.lng, pin.lat],
          },
        })),
      };

      map.addSource("love-lock-pins", {
        type: "geojson",
        data: geojsonData,
        cluster: true,
        clusterMaxZoom: 10,
        clusterRadius: 40,
      });

      // Cluster circles
      map.addLayer({
        id: "clusters",
        type: "circle",
        source: "love-lock-pins",
        filter: ["has", "point_count"],
        paint: {
          "circle-color": "#C9A96E",
          "circle-radius": ["step", ["get", "point_count"], 18, 5, 24, 20, 32],
          "circle-opacity": 0.85,
          "circle-stroke-width": 2,
          "circle-stroke-color": "rgba(201, 169, 110, 0.3)",
        },
      });

      // Cluster count labels
      map.addLayer({
        id: "cluster-count",
        type: "symbol",
        source: "love-lock-pins",
        filter: ["has", "point_count"],
        layout: {
          "text-field": "{point_count_abbreviated}",
          "text-font": ["DIN Pro Medium", "Arial Unicode MS Bold"],
          "text-size": 13,
        },
        paint: {
          "text-color": "#1A1A1A",
        },
      });

      // Individual pin circles
      map.addLayer({
        id: "unclustered-point",
        type: "circle",
        source: "love-lock-pins",
        filter: ["!", ["has", "point_count"]],
        paint: {
          "circle-color": "#C9A96E",
          "circle-radius": 7,
          "circle-stroke-width": 2,
          "circle-stroke-color": "rgba(201, 169, 110, 0.4)",
          "circle-opacity": 0.9,
        },
      });

      // Click on cluster to zoom in
      map.on("click", "clusters", (e) => {
        const features = map.queryRenderedFeatures(e.point, { layers: ["clusters"] });
        const clusterId = features[0].properties.cluster_id;
        map.getSource("love-lock-pins").getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;
          map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom });
        });
      });

      // Click on individual pin to show popup
      map.on("click", "unclustered-point", (e) => {
        const props = e.features[0].properties;
        const coords = e.features[0].geometry.coordinates.slice();
        const location = [props.city, props.country].filter(Boolean).join(", ");

        new mapboxgl.Popup({ offset: 15, maxWidth: "280px", closeButton: true })
          .setLngLat(coords)
          .setHTML(
            `
            ${props.photoUrl ? `<img class="mll-popup__photo" src="${props.photoUrl}" alt="${props.names}" />` : ""}
            <p class="mll-popup__names">${props.names}</p>
            ${location ? `<p class="mll-popup__location">${location}</p>` : ""}
            ${props.message ? `<p class="mll-popup__message">${props.message}</p>` : ""}
            <a href="/products/love-locks" class="mll-popup__cta">
              Design Your Lock
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
            </a>
          `,
          )
          .addTo(map);
      });

      // Cursor changes
      map.on("mouseenter", "clusters", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "clusters", () => {
        map.getCanvas().style.cursor = "";
      });
      map.on("mouseenter", "unclustered-point", () => {
        map.getCanvas().style.cursor = "pointer";
      });
      map.on("mouseleave", "unclustered-point", () => {
        map.getCanvas().style.cursor = "";
      });
    });

    // Update pin count
    const countEl = document.getElementById("mll-pin-count-number");
    if (countEl) {
      animateCount(countEl, samplePins.length);
    }

    // Build location index — group pins by city, then by country, alphabetical
    const locationGrid = document.getElementById("mll-location-grid");
    if (locationGrid) {
      const grouped = {};
      samplePins.forEach((pin) => {
        const key = (pin.city || "Unknown") + "|" + (pin.country || "");
        if (!grouped[key]) {
          grouped[key] = {
            city: pin.city || "Unknown",
            country: pin.country || "",
            lat: pin.lat,
            lng: pin.lng,
            names: [],
          };
        }
        grouped[key].names.push(pin.names);
      });

      const locations = Object.values(grouped);

      // Group locations by country, sort countries alphabetically
      const byCountry = {};
      locations.forEach((loc) => {
        const country = loc.country || "Other";
        if (!byCountry[country]) byCountry[country] = [];
        byCountry[country].push(loc);
      });

      // Sort cities within each country alphabetically
      Object.values(byCountry).forEach((locs) => locs.sort((a, b) => a.city.localeCompare(b.city)));

      // Sort countries alphabetically, but put "United States" first for US audience
      const countryNames = Object.keys(byCountry).sort((a, b) => {
        if (a === "United States") return -1;
        if (b === "United States") return 1;
        return a.localeCompare(b);
      });

      let html = "";
      countryNames.forEach((country) => {
        const locs = byCountry[country];
        html += `<div class="mll-location-country" data-country="${country}">
          <div class="mll-location-country__header">
            <h3 class="mll-location-country__name">${country}</h3>
            <span class="mll-location-country__count">${locs.length} location${locs.length !== 1 ? "s" : ""}</span>
          </div>
          <div class="mll-location-country__cities">
            ${locs
              .map(
                (loc) => `
              <div class="mll-location-card" data-lat="${loc.lat}" data-lng="${loc.lng}" data-city="${loc.city}">
                <div class="mll-location-card__header">
                  <svg class="mll-location-card__pin" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                  <div>
                    <h4 class="mll-location-card__city">${loc.city}</h4>
                  </div>
                </div>
                <div class="mll-location-card__names">${loc.names.join(", ")}</div>
              </div>
            `,
              )
              .join("")}
          </div>
        </div>`;
      });

      locationGrid.innerHTML = html;

      // Click card to fly to location on map
      locationGrid.querySelectorAll(".mll-location-card").forEach((card) => {
        card.addEventListener("click", () => {
          const lat = parseFloat(card.dataset.lat);
          const lng = parseFloat(card.dataset.lng);
          map.flyTo({ center: [lng, lat], zoom: 12, duration: 1500 });
          container.scrollIntoView({ behavior: "smooth", block: "center" });
        });
      });
    }

    // Add pin panel toggle
    const addButton = document.getElementById("mll-add-pin-button");
    const panel = document.getElementById("mll-add-pin-panel");
    const closeButton = document.getElementById("mll-close-panel");

    if (addButton && panel) {
      addButton.addEventListener("click", () => {
        panel.classList.add("is-open");
        // Set timestamp for anti-spam check
        const tsField = document.getElementById("pin-form-ts");
        if (tsField) tsField.value = String(Date.now());
      });
      closeButton.addEventListener("click", () => panel.classList.remove("is-open"));
    }

    // Mini map for location picker in the submit panel
    let miniMap = null;
    let miniMarker = null;

    if (addButton && panel) {
      addButton.addEventListener("click", () => {
        // Initialize mini map on first open
        if (!miniMap) {
          setTimeout(() => {
            miniMap = new mapboxgl.Map({
              container: "pin-mini-map",
              style: "mapbox://styles/mapbox/light-v11",
              center: [0, 30],
              zoom: 1,
              scrollZoom: true,
              projection: "mercator",
            });
            miniMap.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

            // Click mini map to place pin
            miniMap.on("click", (e) => {
              setLocationPin(e.lngLat.lat, e.lngLat.lng);
              // Reverse geocode to get place name
              fetch(
                "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
                  e.lngLat.lng +
                  "," +
                  e.lngLat.lat +
                  ".json?access_token=" +
                  token +
                  "&types=place,locality",
              )
                .then((r) => r.json())
                .then((data) => {
                  if (data.features && data.features[0]) {
                    document.getElementById("pin-location-label").textContent =
                      data.features[0].place_name;
                  }
                })
                .catch(() => {});
            });
          }, 300);
        }
      });
    }

    function setLocationPin(lat, lng) {
      document.getElementById("pin-lat").value = lat.toFixed(6);
      document.getElementById("pin-lng").value = lng.toFixed(6);
      if (miniMap) {
        if (miniMarker) miniMarker.remove();
        miniMarker = new mapboxgl.Marker({ color: "#b8944f" }).setLngLat([lng, lat]).addTo(miniMap);
        miniMap.flyTo({ center: [lng, lat], zoom: 10, duration: 1000 });
      }
    }

    // Location search with Mapbox geocoding
    const searchInput = document.getElementById("pin-location-search");
    const searchResults = document.getElementById("pin-search-results");
    let searchTimeout = null;

    if (searchInput && searchResults) {
      searchInput.addEventListener("input", () => {
        clearTimeout(searchTimeout);
        const q = searchInput.value.trim();
        if (q.length < 2) {
          searchResults.classList.remove("is-open");
          return;
        }
        searchTimeout = setTimeout(() => {
          fetch(
            "https://api.mapbox.com/geocoding/v5/mapbox.places/" +
              encodeURIComponent(q) +
              ".json?access_token=" +
              token +
              "&types=place,locality,neighborhood,address&limit=5",
          )
            .then((r) => r.json())
            .then((data) => {
              if (!data.features || data.features.length === 0) {
                searchResults.classList.remove("is-open");
                return;
              }
              searchResults.innerHTML = data.features
                .map(
                  (f) =>
                    '<div class="mll-map__search-result" data-lng="' +
                    f.center[0] +
                    '" data-lat="' +
                    f.center[1] +
                    '" data-name="' +
                    f.place_name.replace(/"/g, "&quot;") +
                    '">' +
                    f.place_name +
                    "</div>",
                )
                .join("");
              searchResults.classList.add("is-open");

              searchResults.querySelectorAll(".mll-map__search-result").forEach((el) => {
                el.addEventListener("click", () => {
                  const lat = parseFloat(el.dataset.lat);
                  const lng = parseFloat(el.dataset.lng);
                  searchInput.value = el.dataset.name;
                  searchResults.classList.remove("is-open");
                  setLocationPin(lat, lng);
                  document.getElementById("pin-location-label").textContent = el.dataset.name;
                });
              });
            })
            .catch(() => {});
        }, 300);
      });

      // Close results on blur
      document.addEventListener("click", (e) => {
        if (!searchInput.contains(e.target) && !searchResults.contains(e.target)) {
          searchResults.classList.remove("is-open");
        }
      });
    }

    // Character count for message
    const messageField = document.getElementById("pin-message");
    const messageCount = document.getElementById("pin-message-count");
    if (messageField && messageCount) {
      messageField.addEventListener("input", () => {
        messageCount.textContent = messageField.value.length;
      });
    }

    // Photo upload preview
    const photoInput = document.getElementById("pin-photo");
    const photoPreview = document.getElementById("pin-photo-preview");
    const photoPreviewImg = document.getElementById("pin-photo-preview-img");
    const photoPlaceholder = document.getElementById("pin-photo-placeholder");
    const photoRemove = document.getElementById("pin-photo-remove");

    if (photoInput) {
      photoInput.addEventListener("change", (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
          alert("Photo must be under 5MB");
          photoInput.value = "";
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          photoPreviewImg.src = ev.target.result;
          photoPreview.style.display = "block";
          photoPlaceholder.style.display = "none";
        };
        reader.readAsDataURL(file);
      });

      if (photoRemove) {
        photoRemove.addEventListener("click", () => {
          photoInput.value = "";
          photoPreview.style.display = "none";
          photoPlaceholder.style.display = "flex";
        });
      }
    }

    // Form submission
    const form = document.getElementById("mll-add-pin-form");
    if (form) {
      form.addEventListener("submit", async (e) => {
        e.preventDefault();
        const submitBtn = document.getElementById("pin-submit-btn");
        submitBtn.classList.add("is-loading");
        submitBtn.disabled = true;

        try {
          const formData = new FormData(form);

          // For now, submit without photo (photo upload needs server-side handling)
          const response = await fetch(appUrl + "/api/storefront/pins", {
            method: "POST",
            body: formData,
          });

          if (response.ok) {
            form.innerHTML =
              '<div style="text-align:center;padding:3rem 0;">' +
              '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#b8944f" stroke-width="1.5" style="margin-bottom:1rem;">' +
              '<path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>' +
              '<polyline points="22 4 12 14.01 9 11.01"></polyline></svg>' +
              '<p style="font-family:var(--mll-font-heading);font-size:1.6rem;color:#1A1A1A;margin-bottom:1rem;">Thank You</p>' +
              '<p style="font-size:0.85rem;color:#888;line-height:1.6;">Your location has been submitted and will appear on the map after review by our team.</p>' +
              "</div>";
          } else {
            const err = await response.json().catch(() => ({}));
            alert(err.error || "Something went wrong. Please try again.");
            submitBtn.classList.remove("is-loading");
            submitBtn.disabled = false;
          }
        } catch (error) {
          alert("Something went wrong. Please try again.");
          submitBtn.classList.remove("is-loading");
          submitBtn.disabled = false;
        }
      });
    }
  }

  function animateCount(el, target) {
    let current = 0;
    const duration = 1500;
    const start = performance.now();

    function update(now) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      current = Math.round(target * eased);
      el.textContent = current.toLocaleString();
      if (progress < 1) requestAnimationFrame(update);
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        requestAnimationFrame(update);
        observer.disconnect();
      }
    });
    observer.observe(el);
  }
})();
